/**
 * LiveInsightsSectionV2 - Success Story Format
 * Displays restaurant success stories instead of data-heavy analytics
 * Focus on emotional impact and peer success to drive conversions
 */

import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { useInView } from 'framer-motion';
import { TrendingUp, Utensils, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SuccessStoryCardV2 as SuccessStoryCard } from './insights/SuccessStoryCardV2';
import { ROICalculatorModal } from './ROICalculatorModal';
import { containerVariants, fadeInVariants } from '../ui/animations';
import { homepageConfig, getFeaturedStories } from '@/config/homepage.config';

// Get success stories from configuration
const successStories = getFeaturedStories();

// Get social proof from configuration
const socialProof = homepageConfig.socialProof;

export const LiveInsightsSectionV2 = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const navigate = useNavigate();
  const [activeStory, setActiveStory] = useState<number | null>(null);
  const [isROIModalOpen, setIsROIModalOpen] = useState(false);

  const handleActionClick = (index: number) => {
    // Track which story was clicked for analytics
    setActiveStory(index);

    // Navigate based on the story index (reordered for VH-first)
    if (index === 0) {
      // "Stop Missing Calls" - Virtual Host story - go to Virtual Host page
      window.location.href = 'https://dineai.ai/products/virtual-host';
    } else if (index === 1) {
      // "See How It Works" - Revenue story - scroll to How It Works section
      const howItWorksSection = document.getElementById('how-it-works');
      if (howItWorksSection) {
        howItWorksSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (index === 2) {
      // "Start Winning Today" - Competition story - go to signup page
      window.location.href = 'https://dineai.ai/restaurant-menu-search';
    }
  };

  const handleMainCTA = () => {
    navigate('/restaurant-menu-search');
  };

  return (
    <section 
      ref={sectionRef}
      className="py-12 bg-dineai-blue relative overflow-hidden"
    >
      {/* Background elements - matching RevenueImpact section */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-[#43acff]/20 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-white/10 blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section - Clean and consistent */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block bg-dineai-yellow/20 px-4 py-2 rounded-full mb-4">
            <span className="text-dineai-yellow font-medium text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Success Stories
            </span>
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold mb-3 text-white">
            See How Restaurants Are{" "}
            <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
              Winning With AI
            </span>
          </h2>
          
          <p className="text-white/80 text-base max-w-2xl mx-auto">
            Real restaurant owners share their transformation stories
          </p>
        </motion.div>
        
        {/* Success Story Cards */}
        <motion.div 
          className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-16 items-start"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {successStories.map((story, index) => (
            <SuccessStoryCard
              key={index}
              {...story}
              index={index}
              onActionClick={() => handleActionClick(index)}
            />
          ))}
        </motion.div>

        {/* Trust Indicators - Simplified for launch */}
        <motion.div 
          className="text-center mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex flex-wrap justify-center gap-8">
            <div className="flex items-center gap-2 text-white/60">
              <span>✓</span>
              <span>Setup in 5 minutes</span>
            </div>
            <div className="flex items-center gap-2 text-white/60">
              <span>✓</span>
              <span>Cancel anytime</span>
            </div>
          </div>
        </motion.div>

        {/* Main CTA Section - Clean and focused */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
            Ready to Write{" "}
            <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
              Your Success Story?
            </span>
          </h3>
          <p className="text-white/80 text-base mb-8 max-w-2xl mx-auto">
            Join these restaurant owners in transforming their business with AI
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/restaurant-menu-search">
              <Button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all">
                SIGN UP NOW
              </Button>
            </Link>
            <Button 
              onClick={() => setIsROIModalOpen(true)}
              className="bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white hover:border-[#43acff] py-4 sm:py-6 px-6 sm:px-8 text-base sm:text-lg font-medium rounded-full transform hover:scale-105 transition-all">
              ROI CALCULATOR
            </Button>
          </div>
        </motion.div>
      </div>
      
      {/* ROI Calculator Modal */}
      <ROICalculatorModal 
        isOpen={isROIModalOpen} 
        onClose={() => setIsROIModalOpen(false)} 
      />
    </section>
  );
};

export default LiveInsightsSectionV2;